import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useCart } from "./CartContext";
import { useStoreRegion } from "./StoreRegionContext";
import { Minus, Plus, ShoppingBag, Package, ShieldCheck, ArrowLeft, Leaf, Sparkles, Image as ImageIcon } from "lucide-react";
import { FRAGRANCE_PROFILES, DEFAULT_FRAGRANCE_KEY, getFragrance } from "./fragranceProfiles";
import TiltImage from "./TiltImage";
import CandleCareSafety from "./CandleCareSafety";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(1);
  const [fragranceKey, setFragranceKey] = useState(DEFAULT_FRAGRANCE_KEY);
  const [mediaMode, setMediaMode] = useState("photo");
  const [activeImage, setActiveImage] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const { addItem } = useCart();
  const { current, symbol, priceFor, stockState } = useStoreRegion();
  const navigate = useNavigate();

  const fragrance = getFragrance(fragranceKey);

  // Group photos by detected primary colour (order preserved).
  const colorGroups = useMemo(() => {
    const groups = [];
    for (const pc of product?.photo_colors || []) {
      if (!pc.color_name) continue;
      let g = groups.find((x) => x.name === pc.color_name);
      if (!g) {
        g = { name: pc.color_name, hex: pc.hex || "#ccc", images: [] };
        groups.push(g);
      }
      if (!g.images.includes(pc.image_url)) g.images.push(pc.image_url);
    }
    return groups;
  }, [product]);

  const hasColorSelector = colorGroups.length >= 2;

  const displayImages = useMemo(() => {
    if (hasColorSelector) {
      return colorGroups.find((g) => g.name === selectedColor)?.images || [];
    }
    return product?.gallery_images || [];
  }, [hasColorSelector, colorGroups, selectedColor, product]);

  useEffect(() => {
    apiClient
      .get(`/store/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setActiveImage(r.data.image_url);
      })
      .catch(() => setError("Product not found"));
  }, [id]);

  // Pick the default colour once the product (and its groups) are known.
  // Always set the active colour (even single-colour) so the cart/order carries it.
  useEffect(() => {
    if (!product) return;
    if (colorGroups.length >= 1) {
      const main = (product.photo_colors || []).find((pc) => pc.image_url === product.image_url);
      setSelectedColor(main ? main.color_name : colorGroups[0].name);
    } else {
      setSelectedColor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  // When a colour is chosen, swap the main photo to that colour's first image.
  useEffect(() => {
    if (selectedColor) {
      const g = colorGroups.find((x) => x.name === selectedColor);
      if (g?.images?.length) setActiveImage(g.images[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <div className="text-3xl font-black mb-4" style={{ fontFamily: "Outfit" }}>
          {error}
        </div>
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.25em] font-bold hover:text-[#002FA7]"
        >
          ← Back to shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="text-center py-32 text-zinc-400 text-sm">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 md:py-16">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-to-shop"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-zinc-500 hover:text-zinc-900 mb-10 transition"
      >
        <ArrowLeft size={14} strokeWidth={2} /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        {/* Media gallery — flat photo (default) + 3D parallax view */}
        <div data-testid="product-media-gallery">
          {mediaMode === "photo" ? (
            <div className="aspect-square bg-zinc-100 overflow-hidden relative" data-testid="product-image">
              <img
                src={activeImage || product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-flat-photo"
              />
            </div>
          ) : (
            <TiltImage
              src={activeImage || product.image_url}
              alt={product.name}
              data-testid="product-tilt-image"
            />
          )}

          {/* Thumbnail strip — shown when there are multiple photos */}
          {displayImages.length > 1 && (
            <div className="mt-4 flex gap-3 flex-wrap" data-testid="product-thumbnails">
              {displayImages.map((img, i) => (
                <button
                  key={img + i}
                  type="button"
                  onClick={() => setActiveImage(img)}
                  data-testid={`product-thumb-${i}`}
                  aria-label={`View photo ${i + 1}`}
                  className={`w-16 h-16 bg-zinc-100 overflow-hidden border transition ${
                    (activeImage || product.image_url) === img
                      ? "border-zinc-900"
                      : "border-zinc-200 hover:border-zinc-400"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Segmented toggle — Photo ↔ Interactive 3D */}
          <div
            className="mt-4 inline-flex border border-zinc-300 bg-white"
            role="tablist"
            aria-label="Media view"
            data-testid="media-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={mediaMode === "photo"}
              data-testid="media-toggle-photo"
              onClick={() => setMediaMode("photo")}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
                mediaMode === "photo"
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
              style={{ fontFamily: "Outfit" }}
            >
              <ImageIcon size={13} strokeWidth={2} /> Photo
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mediaMode === "interactive"}
              data-testid="media-toggle-interactive"
              onClick={() => setMediaMode("interactive")}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.25em] font-bold border-l border-zinc-300 transition-colors ${
                mediaMode === "interactive"
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`}
              style={{ fontFamily: "Outfit" }}
            >
              <Sparkles size={13} strokeWidth={2} /> Interactive 3D
            </button>
          </div>
        </div>

        {/* Info */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 mb-4">
            {product.niche === "candles" ? "Hand-Poured Candle" : product.niche === "home_decor" ? "Home & Decor" : "Collection"}
          </div>
          <h1
            className="text-4xl md:text-5xl font-black tracking-tighter leading-tight mb-4"
            style={{ fontFamily: "Outfit" }}
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <div
            className="text-2xl font-mono font-bold mb-6"
            data-testid="product-price"
          >
            {symbol}{priceFor(product).toFixed(2)}
            <span className="text-sm text-zinc-400 font-normal ml-2">{current.currency || "USD"}</span>
          </div>

          {(() => {
            const st = stockState(product);
            if (st.code === "sold_out") {
              return (
                <div
                  className="inline-block px-3 py-1 mb-4 text-[10px] uppercase tracking-[0.25em] font-bold bg-zinc-900 text-white"
                  data-testid="stock-badge-sold-out"
                >
                  Sold Out in {current.code}
                </div>
              );
            }
            if (st.code === "low") {
              return (
                <div
                  className="inline-block px-3 py-1 mb-4 text-[10px] uppercase tracking-[0.25em] font-bold bg-amber-500 text-white"
                  data-testid="stock-badge-low"
                >
                  {st.label}
                </div>
              );
            }
            return null;
          })()}

          <div className="h-px bg-zinc-100 my-8" />

          {/* Primary Colour selector — only when a product has 2+ detected colours. */}
          {hasColorSelector && (
            <div className="mb-8" data-testid="color-selector">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.3em] font-semibold luxe-text">
                  Primary Colour
                </div>
                <span className="text-[11px] font-bold tracking-tight" data-testid="selected-color-name">
                  {selectedColor}
                </span>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {colorGroups.map((g) => {
                  const active = g.name === selectedColor;
                  return (
                    <button
                      key={g.name}
                      type="button"
                      onClick={() => setSelectedColor(g.name)}
                      data-testid={`color-swatch-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
                      aria-pressed={active}
                      title={g.name}
                      className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 border transition ${
                        active ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      <span
                        className="w-6 h-6 rounded-full border border-black/10 shrink-0"
                        style={{ backgroundColor: g.hex }}
                        aria-hidden
                      />
                      <span className="text-[11px] font-semibold tracking-tight whitespace-nowrap">
                        {g.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Fragrance Selector — the heart of every Smibha candle. */}
          <div
            className="mb-8 border border-zinc-200 bg-[#FBF8F3]"
            data-testid="fragrance-selector-card"
          >
            <div className="p-5 border-b border-zinc-200">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.3em] font-semibold luxe-text">
                  Custom Fragrance
                </div>
                <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                  {FRAGRANCE_PROFILES.filter((f) => !f.isUnscented).length} House Blends + Unscented
                </span>
              </div>
              <label
                htmlFor="fragrance-select"
                className="sr-only"
              >
                Select fragrance
              </label>
              <div className="relative">
                <select
                  id="fragrance-select"
                  data-testid="fragrance-select"
                  value={fragranceKey}
                  onChange={(e) => setFragranceKey(e.target.value)}
                  className="w-full appearance-none bg-white border border-zinc-300 px-4 py-3.5 pr-10 text-sm font-bold tracking-tight focus:outline-none focus:border-zinc-900 cursor-pointer"
                  style={{ fontFamily: "Outfit" }}
                >
                  {FRAGRANCE_PROFILES.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <svg
                  className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-500"
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </div>
              <p
                className="text-sm text-zinc-700 leading-relaxed mt-4 italic"
                data-testid="fragrance-description"
              >
                {fragrance.description}
              </p>
            </div>

            <div
              className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200"
              data-testid="fragrance-notes"
            >
              {fragrance.isUnscented
                ? (
                  <div className="p-5 sm:col-span-3" data-testid="fragrance-unscented-note">
                    <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-2 font-bold">
                      Fragrance
                    </div>
                    <div className="text-sm text-zinc-900 leading-snug">
                      No fragrance added — a clean, scent-free candle.
                    </div>
                  </div>
                )
                : [
                { label: "Top", value: fragrance.top, tid: "fragrance-top" },
                { label: "Mid", value: fragrance.middle, tid: "fragrance-mid" },
                { label: "Dry", value: fragrance.dry, tid: "fragrance-dry" },
              ].map((n) => (
                <div key={n.label} className="p-5" data-testid={n.tid}>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-2 font-bold">
                    {n.label}
                  </div>
                  <div className="text-sm text-zinc-900 leading-snug">{n.value}</div>
                </div>
              ))}
            </div>

            {(product.burn_time_hours || product.burn_time_label || product.wax_type) && (
              <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200 border-t border-zinc-200">
                {(product.burn_time_hours || product.burn_time_label) && (
                  <div className="p-5" data-testid="burn-time">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-semibold luxe-text mb-1">
                      Burn Time
                    </div>
                    <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
                      {product.burn_time_label || `${product.burn_time_hours}+ Hours`}
                    </div>
                  </div>
                )}
                {product.wax_type && (
                  <div className="p-5" data-testid="wax-type">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-semibold luxe-text mb-1">
                      Wax Type
                    </div>
                    <div className="text-lg font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
                      {product.wax_type}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* The Clean Standard — Paraben- & Phthalate-Free promise. */}
          <div
            className="mb-8 flex items-start gap-4 p-5 border border-emerald-200 bg-emerald-50/60"
            data-testid="clean-standard-banner"
          >
            <span
              className="shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center"
              aria-hidden="true"
            >
              <Leaf size={18} strokeWidth={1.75} className="text-emerald-700" />
            </span>
            <div>
              <div
                className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-800 mb-1"
                style={{ fontFamily: "Outfit" }}
              >
                The Clean Standard
              </div>
              <p className="text-sm text-emerald-900 leading-relaxed">
                Every Smibha Luxe creation is entirely{" "}
                <strong className="font-bold">Paraben- &amp; Phthalate-Free</strong>. Safer for you,
                your loved ones, and your home&rsquo;s air.
              </p>
            </div>
          </div>

          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500 mb-2">
              Description
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed">{product.description}</p>
          </div>

          {product.tags?.length > 0 && (
            <div className="mb-8 flex flex-wrap gap-2">
              {product.tags.map((t) => (
                <span
                  key={t}
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-zinc-200 text-zinc-600"
                >
                  #{t}
                </span>
              ))}
            </div>
          )}

          <div className="h-px bg-zinc-100 my-8" />

          {/* Qty + Add to cart */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-zinc-900">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                data-testid="qty-decrement"
                className="w-11 h-11 hover:bg-zinc-100 flex items-center justify-center"
                aria-label="Decrease quantity"
              >
                <Minus size={14} strokeWidth={2} />
              </button>
              <span
                data-testid="qty-value"
                className="w-10 text-center font-mono font-bold"
              >
                {qty}
              </span>
              <button
                onClick={() => setQty(qty + 1)}
                data-testid="qty-increment"
                className="w-11 h-11 hover:bg-zinc-100 flex items-center justify-center"
                aria-label="Increase quantity"
              >
                <Plus size={14} strokeWidth={2} />
              </button>
            </div>
            <button
              onClick={() =>
                addItem(
                  { ...product, price_usd: priceFor(product) },
                  qty,
                  {
                    fragranceKey: fragrance.key,
                    fragranceName: fragrance.name,
                    colorName: selectedColor || null,
                    colorImage: selectedColor
                      ? colorGroups.find((g) => g.name === selectedColor)?.images?.[0] || null
                      : null,
                  },
                )
              }
              disabled={stockState(product).code === "sold_out"}
              data-testid="add-to-cart-btn"
              className="flex-1 bg-zinc-900 text-white h-11 text-xs uppercase tracking-[0.25em] font-bold hover:bg-black transition inline-flex items-center justify-center gap-2 disabled:bg-zinc-300 disabled:cursor-not-allowed"
            >
              <ShoppingBag size={14} strokeWidth={2} />
              {stockState(product).code === "sold_out" ? "Sold Out" : "Add to Bag"}
            </button>
          </div>

          {/* Trust strip (shipping estimates shown at checkout) */}
          <div className="grid grid-cols-2 gap-4 border-t border-zinc-100 pt-6 mt-6">
            {[
              { icon: Package, label: "Hand-Poured", detail: "Made to order in Ontario" },
              { icon: ShieldCheck, label: "Secure", detail: "Stripe checkout" },
            ].map((x) => (
              <div key={x.label} className="flex items-start gap-2">
                <x.icon size={16} strokeWidth={1.5} className="text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <div className="text-xs font-bold">{x.label}</div>
                  <div className="text-[10px] text-zinc-500">{x.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Candle Care & Safety */}
          <div className="border-t border-zinc-100 pt-6 mt-6">
            <CandleCareSafety compact />
          </div>
        </div>
      </div>
    </div>
  );
}
